/**
 * Combat helpers: target validation, question draw, answer resolution.
 *
 * These are pure functions consumed by the reducer's execute-tests phase.
 * Combat itself is a small state machine encoded in `AttackState`; this
 * module focuses on the inputs the reducer needs at each transition.
 */

import type {
  AttackState,
  ClientSafeQuestion,
  Deck,
  GameState,
  PlayerId,
  QuestionId,
  TerritoryRuntimeState,
} from '@qd/protocol';
import type { ActiveMap } from '@qd/map';
import type { Question } from '@qd/questions';
import { lookupQuestion, questionIdsForTier, toClientSafeQuestion } from './questionRegistry.js';
import type { Prng } from './prng.js';
import { shuffledChoicesFor } from './answerShuffle.js';

/**
 * Identify legal attack targets from `sourceId` for `attackerId`.
 *
 * Rules:
 * - Source must be controlled by attacker.
 * - Source must hold ≥2 cubes (one always remains as garrison).
 * - Target must be adjacent on the active map.
 * - Target must NOT be controlled by attacker (no self-attacks).
 */
export function validAttackTargets(
  sourceId: string,
  attackerId: PlayerId,
  state: GameState,
  mapDef: ActiveMap,
): readonly string[] {
  const source = state.territories[sourceId];
  if (!source) return [];
  if (source.controllingPlayerId !== attackerId) return [];
  if (source.cubeCount < 2) return [];

  const adj = mapDef.adjacency.get(sourceId as never) ?? [];
  const out: string[] = [];
  for (const id of adj) {
    const t = state.territories[id];
    if (!t) continue;
    if (t.controllingPlayerId === attackerId) continue;
    out.push(id);
  }
  return out;
}

/**
 * Whether `playerId` has at least one legal attack from any of their
 * territories. Returns false when every owned territory either has
 * fewer than 2 cubes or no adjacent non-self target.
 *
 * Used by the client to surface the "rescue deploy" UI when a player
 * is stuck mid-turn during simplified play, where reserves accumulate
 * but never auto-deploy. The reducer also consults this when accepting
 * a rescue DEPLOY_CUBES outside the normal document-deploy phase.
 */
export function canPlayerAttack(
  playerId: PlayerId,
  state: GameState,
  mapDef: ActiveMap,
): boolean {
  for (const t of Object.values(state.territories)) {
    if (t.controllingPlayerId !== playerId) continue;
    if (t.cubeCount < 2) continue;
    const targets = validAttackTargets(t.territoryId, playerId, state, mapDef);
    if (targets.length > 0) return true;
  }
  return false;
}

/**
 * Draw the next question for combat. Determinism:
 *  - The question pool is keyed by tier (matches territory risk → required tier).
 *  - We pick the *next* unused question from that pool by walking the question
 *    deck (questions are consumed one-by-one and added to discard).
 *  - When the deck is exhausted, we reshuffle discard back into draw using `prng`.
 */
export interface DrawnQuestion {
  readonly full: Question;
  readonly clientSafe: ClientSafeQuestion;
  readonly nextDeck: Deck<QuestionId>;
}

/**
 * Map territory risk-level to question tier.
 * - low → tier 1
 * - medium → tier 2
 * - high → tier 3
 */
export function tierForRisk(risk: 'low' | 'medium' | 'high'): 1 | 2 | 3 {
  switch (risk) {
    case 'low':
      return 1;
    case 'medium':
      return 2;
    case 'high':
      return 3;
  }
}

/**
 * Draw the top question from a deck, filtered by tier.
 *
 * If the top of the deck doesn't match `tier`, we walk forward and pull the
 * next match; if no match exists, we fall back to any tier (so combat never
 * stalls). This keeps the operation pure and deterministic given the same
 * deck snapshot.
 *
 * `gameSeed` is the GameState `seed`. It's used to derive a per-question,
 * stable Fisher-Yates permutation for the client-safe choice list so that
 * "always pick A" is no longer a winning strategy. The canonical
 * `correctIndex` in @qd/questions stays the source of truth — the
 * reducer's ANSWER_QUESTION handler maps the submitted index back through
 * the same permutation when validating.
 */
export function drawNextQuestion(
  deck: Deck<QuestionId>,
  tier: 1 | 2 | 3,
  prng: Prng,
  gameSeed: string,
): DrawnQuestion | null {
  let draw = deck.draw.slice();
  let discard = deck.discard.slice();

  // Reshuffle if empty.
  if (draw.length === 0) {
    if (discard.length === 0) return null;
    draw = prng.shuffle(discard);
    discard = [];
  }

  // Pull the first question matching `tier`. If none, fall back to any.
  const tierIds = new Set(questionIdsForTier(tier));
  let pickIdx = draw.findIndex((id) => tierIds.has(id));
  if (pickIdx === -1) pickIdx = 0;

  const drawnId = draw[pickIdx]!;
  const remaining = draw.slice(0, pickIdx).concat(draw.slice(pickIdx + 1));
  const newDiscard = [...discard, drawnId];

  const full = lookupQuestion(drawnId);
  if (!full) return null;

  // Build the client-safe projection. For shufflable kinds, replace the
  // baseline choices with the seeded permutation. The full Question is
  // unchanged — only the projection sent to the client.
  const baseClientSafe = toClientSafeQuestion(full);
  const shuffled = shuffledChoicesFor(full, gameSeed);
  const clientSafe: ClientSafeQuestion = shuffled
    ? { ...baseClientSafe, choices: shuffled.choices }
    : baseClientSafe;

  return {
    full,
    clientSafe,
    nextDeck: { draw: remaining, discard: newDiscard },
  };
}

/**
 * Mutate a territory's cube count by `delta`. Returns the new state.
 * Clamps at zero (defender depletion is a separate flow).
 */
export function applyCubeDelta(
  t: TerritoryRuntimeState,
  delta: number,
): TerritoryRuntimeState {
  const next = Math.max(0, t.cubeCount + delta);
  return { ...t, cubeCount: next };
}

/** Whether a combatActive `AttackState` is the current state. */
export function isCombatActive(state: AttackState): boolean {
  return state.kind === 'combatActive';
}
